import type { Message } from '../../../i18n/types';

/**
 * Texts of the automation accounts pages. Spread into the app dictionaries
 * (i18n/en.ts, i18n/ru.ts); keys start with `automation.accounts.`.
 * The Russian ones keep TestRun's own wording.
 */
export const automationAccountsEn = {
  'automation.accounts.title': 'Accounts',
  'automation.accounts.subtitle':
    "The team's test account pool, checking an account on the stand, test payments, registering a new client and the OTP for a manual test.",
  'automation.accounts.tab.pool': 'Pool',
  'automation.accounts.tab.readiness': 'Ready for tests',
  'automation.accounts.tab.check': 'Check',
  'automation.accounts.tab.register': 'Registration',
  'automation.accounts.tab.otp': 'OTP',
  'automation.accounts.phone': 'Phone',
  'automation.accounts.iin': 'IIN',
  'automation.accounts.bin': 'BIN',
  'automation.accounts.phonePlaceholder': '+7 700 000 00 00',
  'automation.accounts.digits12': '12 digits',
  'automation.accounts.noLabel': 'no label',
  'automation.accounts.actions': 'Actions',
  'automation.accounts.seconds': '{value} s',
  'automation.accounts.ms': '{value} ms',
  'automation.accounts.readiness.ok': 'fits',
  'automation.accounts.readiness.warn': 'with reservations',
  'automation.accounts.readiness.fail': 'does not fit',
  'automation.accounts.readiness.unknown': 'unknown',

  // ─── the pool ───
  'automation.accounts.pool.search': 'Search the pool',
  'automation.accounts.pool.searchPlaceholder': 'Label, company, phone, IIN',
  'automation.accounts.pool.fitFilter': 'Fits for',
  'automation.accounts.pool.fitAny': 'Fits for: all',
  'automation.accounts.pool.fitFor': 'Fits for: {what}',
  'automation.accounts.pool.col.payments': 'Payments',
  'automation.accounts.pool.col.inBetween': 'Between accounts',
  'automation.accounts.pool.col.largeSum': 'Large sum',
  'automation.accounts.pool.col.deposit': 'Deposit',
  'automation.accounts.pool.col.salary': 'Salary',
  'automation.accounts.pool.col.loan': 'Loan',
  'automation.accounts.pool.col.cards': 'Cards',
  'automation.accounts.pool.col.ai': 'AI',
  'automation.accounts.pool.checking': 'Checking {done} of {total}…',
  'automation.accounts.pool.checkedAll': 'All checked {when}',
  'automation.accounts.pool.errors': { one: '{count} error', other: '{count} errors' },
  'automation.accounts.pool.checkAll': 'Check all',
  'automation.accounts.pool.checkAllHint':
    'Re-checks every pool account on the stand, one by one in the background',
  'automation.accounts.pool.checkAllFailed': '«Check all» did not start',
  'automation.accounts.pool.empty': 'The pool is empty',
  'automation.accounts.pool.emptyHint':
    'Check an account on the «Check» tab or upload a list with «Import».',
  'automation.accounts.pool.account': 'Account',
  'automation.accounts.pool.phoneIin': 'Phone / IIN',
  'automation.accounts.pool.payer': 'Payer account',
  'automation.accounts.pool.payerHint':
    'The main account, or if none is set — the first open tenge account, as in the payments tests',
  'automation.accounts.pool.checked': 'Checked',
  'automation.accounts.pool.payerMain': 'main',
  'automation.accounts.pool.noMain': 'no main account',
  'automation.accounts.pool.noData': 'no data',
  'automation.accounts.pool.errorAt': 'error {when}',
  'automation.accounts.pool.noMatch': 'Nothing matches the filter.',
  'automation.accounts.pool.spent': 'spent: {classes}',
  'automation.accounts.pool.spentHint':
    'Given to one-off classes — not offered to them again: {list}. «Ready for tests» → «Back in use».',
  'automation.accounts.pool.repairing': 'repairing: {what}…',
  'automation.accounts.pool.listBroken': 'accounts do not load — not given to tests',
  'automation.accounts.pool.listBrokenHint':
    '{reason}. Such an account is not given to tests: without the list of accounts they would fail for a reason not their own.',
  'automation.accounts.pool.listBrokenReason': 'the list of accounts does not load',

  // ─── import ───
  'automation.accounts.import.button': 'Import',
  'automation.accounts.import.file': 'CSV file',
  'automation.accounts.import.template': 'Template',
  'automation.accounts.import.poolTitle': 'Import accounts into the pool',
  'automation.accounts.import.poolHint':
    'One account per line: phone; IIN; BIN; label (BIN only for TOO). A CSV from Excel works. The accounts appear in the pool at once; the check runs in the background, one by one.',
  'automation.accounts.import.poolPlaceholder':
    '+70000000001; 000000000001; ; IP: cards + deposit\n+70000000002; 000000000002; 000000000003; TOO: QR',
  'automation.accounts.import.toImport': 'To import: {count}',
  'automation.accounts.import.withError': {
    one: '{count} with an error',
    other: '{count} with errors',
  },
  'automation.accounts.import.badLine': 'line {line}: {reason} — {text}',
  'automation.accounts.import.more': 'and {count} more',
  'automation.accounts.import.problem.excelNumber':
    'Excel wrote the number as 9.00E+11 — make the IIN and BIN columns text',
  'automation.accounts.import.problem.phone': 'phone',
  'automation.accounts.import.problem.iin': 'IIN — 12 digits',
  'automation.accounts.import.problem.bin': 'BIN — 12 digits',
  'automation.accounts.import.problem.kind': 'kind: IP, TOO, MF or SIMP',
  'automation.accounts.import.problem.companyBin': 'a company needs a BIN — 12 digits',
  'automation.accounts.import.addAndCheck': 'Add and check',
  'automation.accounts.import.add': 'Add',
  'automation.accounts.import.failed': 'The import failed',
  'automation.accounts.import.poolDone': 'Added {added}, already in the pool {updated}',
  'automation.accounts.import.rejected': 'rejected {count}',
  'automation.accounts.import.checkStarted':
    'The check runs in the background — the progress is in the pool header. Whoever cannot log in stays marked «error».',
  'automation.accounts.import.checkWasRunning':
    'A check was already running — check the new accounts with «Check all» after it.',
  'automation.accounts.import.regTitle': 'Import into «for registration»',
  'automation.accounts.import.regHint':
    'One per line: kind; phone; IIN; BIN; label. Kind — IP, TOO, MF or SIMP (another legal form); a BIN for all but IP. make_import.py reg gives the file. Those already in the pool are skipped: they are registered.',
  'automation.accounts.import.regPlaceholder':
    'IP; +70000000001; 000000000001; ; line 2528\nMF; +70000000002; 000000000002; 000000000003; line 4410',
  'automation.accounts.import.regDone': 'Added {added}, updated {updated}',
  'automation.accounts.import.pooled': 'already in the pool (registered) {count}',
  'automation.accounts.import.templateLine1':
    'Import of accounts into the TestRun pool: one account per line. BIN only for TOO, empty for IP.',
  'automation.accounts.import.templateLine2':
    'Lines with # and the header are skipped. The values below are an example — replace them with yours.',
  'automation.accounts.import.templateHeader': 'phone;IIN;BIN;label',
  'automation.accounts.import.templateLabel1': 'IP: cards + deposit',
  'automation.accounts.import.templateLabel2': 'TOO: Freedom QR',
  'automation.accounts.import.templateFile': 'account-import-template.csv',

  // ─── the night watch log ───
  'automation.accounts.changes.title': 'Changes in accounts',
  'automation.accounts.changes.history': 'Change history',
  'automation.accounts.changes.worse': 'of them {count} worse for tests',
  'automation.accounts.changes.period': 'Period',
  'automation.accounts.changes.days': { one: '24 hours', other: '{count} days' },
  'automation.accounts.changes.none': {
    one: 'No changes in the last 24 hours.',
    other: 'No changes in {count} days.',
  },
  'automation.accounts.changes.watchOn':
    'The watch checks the pool every day at {at} ({tz}) and only reads data; last pass — {last}, next — {next}. Manual checks land here too.',
  'automation.accounts.changes.watchOff':
    'The night pass is off (WATCH_AT); changes found by manual checks land here.',
  'automation.accounts.changes.never': 'not yet',
  'automation.accounts.changes.byWatch': 'night check',
  'automation.accounts.changes.byUser': 'checked by {user}',
  'automation.accounts.changes.account': 'Account',
  'automation.accounts.changes.level.bad': 'worse for tests',
  'automation.accounts.changes.level.good': 'fits again',
  'automation.accounts.changes.level.info': 'for information',

  // ─── check ───
  'automation.accounts.check.phoneHint': 'With «+» or without, through 8 works too — it becomes +7',
  'automation.accounts.check.binPlaceholder': 'only for TOO',
  'automation.accounts.check.fresh':
    'Log in anew (OTP and biometry), do not take the token from memory',
  'automation.accounts.check.submit': 'Check',
  'automation.accounts.check.loggingIn': 'Logging in…',
  'automation.accounts.check.slowHint':
    'The first login takes up to half a minute: the SMS code comes from Kafdrop, biometry passes in test mode.',
  'automation.accounts.check.failed': 'Could not check',
  'automation.accounts.check.inPool': 'The account is in the team pool —',
  'automation.accounts.check.openCard': 'open its card, set a label',

  // ─── a check result ───
  'automation.accounts.result.noCompany': 'Company not determined',
  'automation.accounts.result.accounts': 'Accounts',
  'automation.accounts.result.cards': 'Cards',
  'automation.accounts.result.cardsOpen': 'Cards · {open} open',
  'automation.accounts.result.deposits': 'Deposits',
  'automation.accounts.result.loans': 'Loans',
  'automation.accounts.result.employees': 'Employees',
  'automation.accounts.result.toSign': 'To sign',
  'automation.accounts.result.fitsFor': 'Fits for',
  'automation.accounts.result.account': 'Account',
  'automation.accounts.result.number': 'Number',
  'automation.accounts.result.balance': 'Balance',
  'automation.accounts.result.marks': 'Marks',
  'automation.accounts.result.main': 'main',
  'automation.accounts.result.special': 'special account',
  'automation.accounts.result.blocked': 'blocked',
  'automation.accounts.result.accountsNotLoaded': 'The accounts did not load',
  'automation.accounts.result.accountsNone': 'No accounts',
  'automation.accounts.result.loansNotLoaded': 'The loans did not load',
  'automation.accounts.result.loansNone': 'No loans',
  'automation.accounts.result.cardsNotLoaded': 'The cards did not load',
  'automation.accounts.result.cardsNone': 'No cards',
  'automation.accounts.result.depositsNotLoaded': 'The deposits did not load',
  'automation.accounts.result.depositsNone': 'No deposits',
  'automation.accounts.result.loanClosed': 'closed',
  'automation.accounts.result.loanActive': 'active',
  'automation.accounts.result.overdue': 'overdue',
  'automation.accounts.result.debt': 'debt {amount}',
  'automation.accounts.result.nextPayment': 'payment {amount}',
  'automation.accounts.result.onDay': 'on {day}',
  'automation.accounts.result.paid': 'paid {paid} of {total}',
  'automation.accounts.result.forAutotests': 'For autotests',
  'automation.accounts.result.counterparties': 'Counterparties',
  'automation.accounts.result.aiOnboarding': 'AI onboarding',
  'automation.accounts.result.done': 'done',
  'automation.accounts.result.notDone': 'not done',
  'automation.accounts.result.aiLimits': 'AI limits',
  'automation.accounts.result.notSet': 'not set',
  'automation.accounts.result.endpoints': 'Stand endpoints',
  'automation.accounts.result.requests': { one: '{count} request', other: '{count} requests' },
  'automation.accounts.result.not200': 'not 200 — {count}',
  'automation.accounts.result.all200': 'all 200',
  'automation.accounts.result.error': 'error',
  'automation.accounts.result.cannotShow': 'Could not show the result',
  'automation.accounts.result.cannotShowHint':
    '{message}. Send this text: the stand gave a field in an unexpected shape.',

  // ─── actions on the stand ───
  'automation.accounts.act.title': 'Actions',
  'automation.accounts.act.subtitle':
    'Real operations on the stand signed with an OTP; the money stays in the company. Balances are as of the check.',
  'automation.accounts.act.failed': 'Did not work',
  'automation.accounts.act.waitingOtp': 'Waiting for the OTP…',
  'automation.accounts.act.realPaymentOtp':
    "A real payment on the stand: an OTP goes to the account's phone.",
  'automation.accounts.act.realTransferOtp': 'A real transfer on the stand, signed with an OTP.',
  'automation.accounts.act.refreshBalances': 'Refresh balances',
  'automation.accounts.act.refreshCheck': 'Refresh the check',
  'automation.accounts.act.paymentDone': '{amount}: «{from}» → «{to}» — {status}',
  'automation.accounts.act.total': 'total {time}',
  'automation.accounts.act.transfer.title': 'Transfer between own accounts',
  'automation.accounts.act.transfer.hint':
    'IN_BETWEEN, KNP 342, signed with an OTP — like the payments regression.',
  'automation.accounts.act.transfer.from': 'From',
  'automation.accounts.act.transfer.to': 'To',
  'automation.accounts.act.transfer.amount': 'Amount',
  'automation.accounts.act.transfer.go': 'Transfer',
  'automation.accounts.act.transfer.needTwo': 'Two open accounts in one currency are needed',
  'automation.accounts.act.transfer.positive': 'The amount is a positive number',
  'automation.accounts.act.transfer.notEnough': '«{account}» has less than {amount}',
  'automation.accounts.act.transfer.confirm': 'Transfer {amount} from «{from}» to «{to}»?',
  'automation.accounts.act.transfer.failed': 'The transfer did not go through',
  'automation.accounts.act.transfer.noAccounts': 'no open accounts',
  'automation.accounts.act.transfer.noSameCurrency': 'no account in the same currency',
  'automation.accounts.act.topUp.title': 'Top up the payer account',
  'automation.accounts.act.topUp.hint':
    'For a large sum. «{account}»{main}: {balance}; the donor is the richest own account, 1 ₸ stays on it.',
  'automation.accounts.act.topUp.isMain': ' (main)',
  'automation.accounts.act.topUp.target': 'Up to',
  'automation.accounts.act.topUp.presetIp': '6 mln',
  'automation.accounts.act.topUp.presetIpHint': 'large sum for IP: threshold 5 000 001 ₸',
  'automation.accounts.act.topUp.presetToo': '55 mln',
  'automation.accounts.act.topUp.presetTooHint': 'large sum for TOO: threshold 50 000 000 ₸',
  'automation.accounts.act.topUp.go': 'Top up',
  'automation.accounts.act.topUp.running': 'Transferring…',
  'automation.accounts.act.topUp.noPayer': 'No open tenge account',
  'automation.accounts.act.topUp.positive': 'The target is a positive number',
  'automation.accounts.act.topUp.enough':
    'The payer account already has {amount} — not less than the target',
  'automation.accounts.act.topUp.noDonor':
    'Nothing to top up from: no other open accounts with money in the same currency',
  'automation.accounts.act.topUp.plan': 'We will transfer {amount} from «{donor}»',
  'automation.accounts.act.topUp.planShort':
    '; {amount} will still be missing — the next press takes the next account',
  'automation.accounts.act.topUp.confirm': 'Top up the payer account «{account}» to {amount}?',
  'automation.accounts.act.topUp.failed': 'Could not top up',
  'automation.accounts.act.topUp.result':
    'Payer account: was {before}, now ≈ {after}, target {target}.',
  'automation.accounts.act.topUp.shortNext':
    '{amount} short — press again, the next donor is «{donor}».',
  'automation.accounts.act.topUp.shortNone': '{amount} short — no other accounts with money.',
  'automation.accounts.act.main.title': 'Main account',
  'automation.accounts.act.main.hint':
    'Now: {current}. AI needs it — the assistant pays from the main account; payments and other tests do not.',
  'automation.accounts.act.main.unset': 'not set',
  'automation.accounts.act.main.choose': 'Make main',
  'automation.accounts.act.main.noOther': 'no other open tenge account',
  'automation.accounts.act.main.set': 'Make main',
  'automation.accounts.act.main.remove': 'Remove',
  'automation.accounts.act.main.removeConfirm': 'Take the «main» mark off «{account}»?',
  'automation.accounts.act.main.removeHint':
    'The AI tests pay from the main account and fail without it. No OTP.',
  'automation.accounts.act.main.removed': 'The mark is off «{account}»',
  'automation.accounts.act.main.isNow': 'The main account is «{account}»',
  'automation.accounts.act.set.title': 'Set of test payments',
  'automation.accounts.act.set.hint':
    'Own accounts, SuperApp card, deposit there and back, tax by KBK, salary. A kind without data is skipped.',
  'automation.accounts.act.set.start': 'Start',
  'automation.accounts.act.set.again': 'Again',
  'automation.accounts.act.set.running': 'Running…',
  'automation.accounts.act.set.confirm':
    'Make the set of test payments: own accounts, own SuperApp card, deposit there and back, tax by KBK, salary to one employee?',
  'automation.accounts.act.set.confirmHint':
    'Six real payments on the stand of 10–100 ₸, an OTP for each. About a minute.',
  'automation.accounts.act.set.notStarted': 'The set did not start',
  'automation.accounts.act.set.lost': 'Lost the run',
  'automation.accounts.act.set.noPayments': 'Did not get to the payments',
  'automation.accounts.act.set.itemRunning': 'creating, waiting for the OTP, signing…',
  'automation.accounts.act.set.itemPending': 'waiting',
  'automation.accounts.act.set.skipped': 'skipped: {reason}',
  'automation.accounts.act.set.summary': '{ok} of {total} passed',
  'automation.accounts.act.set.failedCount': '{count} failed',
  'automation.accounts.act.set.skippedCount': '{count} skipped',
  'automation.accounts.act.set.status.pending': 'waiting',
  'automation.accounts.act.set.status.running': 'running',
  'automation.accounts.act.set.status.ok': 'passed',
  'automation.accounts.act.set.status.fail': 'failed',
  'automation.accounts.act.set.status.skip': 'skipped',
  'automation.accounts.act.cleanup.title': 'Cleanup',
  'automation.accounts.act.cleanup.hint':
    'Unsigned payments (NEW) left by failed tests, and drafts. What was found is shown first.',
  'automation.accounts.act.cleanup.find': 'Find',
  'automation.accounts.act.cleanup.refresh': 'Refresh',
  'automation.accounts.act.cleanup.listFailed': 'The list did not load',
  'automation.accounts.act.cleanup.clean': 'Clean: no unsigned payments and no drafts.',
  'automation.accounts.act.cleanup.unsigned': 'Unsigned · {count}',
  'automation.accounts.act.cleanup.drafts': 'Drafts · {count}',
  'automation.accounts.act.cleanup.payment': 'payment',
  'automation.accounts.act.cleanup.draft': 'draft',
  'automation.accounts.act.cleanup.fresh': 'younger than 2 minutes — left alone',
  'automation.accounts.act.cleanup.cancelN': {
    one: 'cancel {count} unsigned payment',
    other: 'cancel {count} unsigned payments',
  },
  'automation.accounts.act.cleanup.deleteN': {
    one: 'delete {count} draft',
    other: 'delete {count} drafts',
  },
  'automation.accounts.act.cleanup.confirm': 'Really {what}?',
  'automation.accounts.act.cleanup.confirmHint':
    'This cannot be undone. If an autotest is running on this account right now, wait until it finishes.',
  'automation.accounts.act.cleanup.go': 'Clean up',
  'automation.accounts.act.cleanup.failed': 'The cleanup failed',
  'automation.accounts.act.cleanup.result': 'Cancelled {cancelled}, drafts deleted {deleted}',
  'automation.accounts.act.cleanup.resultSkipped': 'skipped {count}',
  'automation.accounts.act.cleanup.resultFailed': { one: '{count} error', other: '{count} errors' },
  'automation.accounts.act.otp.title': 'OTP code for a manual login',
  'automation.accounts.act.otp.hint':
    'Request a code in the app for {phone} — the last one from Kafdrop shows here.',
  'automation.accounts.act.otp.show': 'Show code',
  'automation.accounts.act.otp.refresh': 'Refresh',
  'automation.accounts.payStatus.IN_PROGRESS_AT_BANK': 'signed, being processed by the bank',
  'automation.accounts.payStatus.SIGNED': 'signed',
  'automation.accounts.payStatus.SUCCEED': 'completed',
  'automation.accounts.payStatus.NEW': 'created, not signed',
  'automation.accounts.payStatus.unknown': 'status unknown',

  // ─── the OTP tab ───
  'automation.accounts.otp.submit': 'Show the last code',
  'automation.accounts.otp.hint':
    'For a manual test: request a code in the app — it appears here (from Kafdrop, the last 10 minutes). For three minutes the page refreshes by itself.',
  'automation.accounts.otp.none': 'No code to this phone in the last 10 minutes',
  'automation.accounts.otp.code': 'OTP code',
  'automation.accounts.otp.failed': 'Did not get the code',
  'automation.accounts.otp.watching': 'refreshing',
  'automation.accounts.otp.asking': 'asking…',

  // ─── an account's page ───
  'automation.accounts.detail.back': 'Account pool',
  'automation.accounts.detail.noLabel': 'No label',
  'automation.accounts.detail.label': 'Label',
  'automation.accounts.detail.labelPlaceholder': 'e.g. «main IP payments», «AI», «TOO 50 mln»',
  'automation.accounts.detail.editLabel': 'Edit',
  'automation.accounts.detail.setLabel': 'Set a label',
  'automation.accounts.detail.inPoolSince': 'Added to the pool {when} ({by})',
  'automation.accounts.detail.checked': 'checked {when}',
  'automation.accounts.detail.recheck': 'Re-check',
  'automation.accounts.detail.rechecking': 'Checking…',
  'automation.accounts.detail.recheckHint':
    'Logs into the account on the stand again; the TSP contract check creates and cancels a 1 ₸ QR',
  'automation.accounts.detail.remove': 'Remove from pool',
  'automation.accounts.detail.removeConfirm': 'Remove the account from the pool?',
  'automation.accounts.detail.removeHint': 'Nothing happens to it on the stand.',
  'automation.accounts.detail.removed': 'Removed from the pool',
  'automation.accounts.detail.lastFailed': 'The last check failed',
  'automation.accounts.detail.noCheck': 'No successful check yet.',

  // ─── registration ───
  'automation.accounts.reg.kind.IP': 'IP',
  'automation.accounts.reg.kind.TOO': 'TOO',
  'automation.accounts.reg.kind.MF': 'TOO, several founders',
  'automation.accounts.reg.kind.SIMP': 'another legal form',
  'automation.accounts.reg.free': '{count} free',
  'automation.accounts.reg.nextFree': 'Next free',
  'automation.accounts.reg.nextFreeHint':
    'Fill the form with the next free account from «for registration»',
  'automation.accounts.reg.phoneHint': 'With «+» or without, through 8 works too',
  'automation.accounts.reg.needTitle': 'What is needed',
  'automation.accounts.reg.need': 'An IIN and a phone not yet registered in Freedom Business.',
  'automation.accounts.reg.need.IP':
    'The IIN of an active IP, otherwise the KGD status check rejects the application.',
  'automation.accounts.reg.need.TOO':
    "For TOO a BIN too; an IIN just registered as an IP will not do. The eGov step waits for a code from 1414 on the director's real phone.",
  'automation.accounts.reg.need.MF':
    'The director of a TOO with several founders: the check without an OTP answers LLP_004_GBDUL. The TOO has an open account.',
  'automation.accounts.reg.need.SIMP':
    'The head of a company of another legal form (JSC, cooperative, institution — not IP and not TOO) and its BIN; the company has an open account. For an ordinary TOO the service answers LLP_006_GBDUL.',
  'automation.accounts.reg.needTail':
    'An application sent to manual AML review is not closed by the autotest. The run is an authenticate_api_testing pipeline ({className}), 2–4 minutes.',
  'automation.accounts.reg.project': 'Project: {name}',
  'automation.accounts.reg.submit': 'Register',
  'automation.accounts.reg.starting': 'Starting…',
  'automation.accounts.reg.confirm': 'Register {kind} on preprod: {who}?',
  'automation.accounts.reg.confirmHint':
    'A real client will be created. After success the IIN is taken: registering with it again fails at the first step.',
  'automation.accounts.reg.marksError':
    'The run started, but TestRun was not told about the account: {error}',
  'automation.accounts.reg.noProject': 'The registration project is not connected',
  'automation.accounts.reg.noProjectHint':
    'Add authenticate_api_testing to one of the teams: registration runs its tests RegistrationIe, RegistrationLlp, RegistrationLlpMultiFounder and RegistrationSimplifiedAccess.',
  'automation.accounts.reg.projectsFailed': 'The projects did not load',
  'automation.accounts.reg.listTitle': 'For registration',
  'automation.accounts.reg.listHint':
    'Unregistered clients for the registration classes. The run window issues them by itself (testdata registration); after the run TestRun checks without an OTP whether the client was created: yes — the account goes to the pool, no — it is free again.',
  'automation.accounts.reg.settle': 'Check issued',
  'automation.accounts.reg.settleCount': 'Check issued ({count})',
  'automation.accounts.reg.settleHint': 'Check the issued ones whose run has finished',
  'automation.accounts.reg.counts': '{kind}: {free} free',
  'automation.accounts.reg.countIssued': '{count} issued',
  'automation.accounts.reg.countRegistered': '{count} registered',
  'automation.accounts.reg.statusFilter': 'Status',
  'automation.accounts.reg.allStatuses': 'All statuses',
  'automation.accounts.reg.status.free': 'free',
  'automation.accounts.reg.status.issued': 'issued',
  'automation.accounts.reg.status.registered': 'registered',
  'automation.accounts.reg.status.used': 'used up',
  'automation.accounts.reg.status.unsuitable': 'unsuitable',
  'automation.accounts.reg.statusHint.free': 'not registered — can be issued',
  'automation.accounts.reg.statusHint.issued':
    'issued to a run; after it TestRun checks the outcome',
  'automation.accounts.reg.statusHint.registered':
    'the client is created — the account goes to the pool',
  'automation.accounts.reg.statusHint.used':
    'the outcome cannot be checked without an OTP — decide by hand',
  'automation.accounts.reg.statusHint.unsuitable': 'registration with this account is impossible',
  'automation.accounts.reg.empty':
    'The list is empty. Candidates are picked by bot/var/clients: pick.py → probe.mjs → make_import.py reg; upload the resulting CSV with «Import».',
  'automation.accounts.reg.kindColumn': 'Kind',
  'automation.accounts.reg.who': 'Phone / IIN / BIN',
  'automation.accounts.reg.lastCheck': 'Last check',
  'automation.accounts.reg.inPool': 'in the pool',
  'automation.accounts.reg.poolError': 'not added to the pool: {error}',
  'automation.accounts.reg.verifiedYes': 'registered',
  'automation.accounts.reg.verifiedNo': 'not registered',
  'automation.accounts.reg.check': 'Check',
  'automation.accounts.reg.checkHint': 'authenticate without an OTP: no SMS goes out',
  'automation.accounts.reg.release': 'Return',
  'automation.accounts.reg.releaseHint':
    'Issue it again: registration with this account did not go through',
  'automation.accounts.reg.remove': 'Remove',
  'automation.accounts.reg.removeLabel': 'Remove {phone} from the list',
  'automation.accounts.reg.removeConfirm': 'Remove {phone} from the registration list?',
  'automation.accounts.reg.removeHint': 'Nothing happens to it on the stand.',
  'automation.accounts.reg.noneWithStatus': 'No accounts with this status.',

  // ─── «ready for tests» ───
  'automation.accounts.readinessTab.project': 'Project',
  'automation.accounts.readinessTab.noProjects': 'No projects',
  'automation.accounts.readinessTab.noProjectsHint':
    "Readiness is counted for a project's classes: add a project to a team first.",
} as const;

export const automationAccountsRu: Record<keyof typeof automationAccountsEn, Message> = {
  'automation.accounts.title': 'Аккаунты',
  'automation.accounts.subtitle':
    'Пул тестовых аккаунтов команды, проверка аккаунта на стенде, пробные платежи, регистрация нового клиента и код OTP для ручного теста.',
  'automation.accounts.tab.pool': 'Пул',
  'automation.accounts.tab.readiness': 'Готовность к тестам',
  'automation.accounts.tab.check': 'Проверка',
  'automation.accounts.tab.register': 'Регистрация',
  'automation.accounts.tab.otp': 'OTP',
  'automation.accounts.phone': 'Телефон',
  'automation.accounts.iin': 'ИИН',
  'automation.accounts.bin': 'БИН',
  'automation.accounts.phonePlaceholder': '+7 700 000 00 00',
  'automation.accounts.digits12': '12 цифр',
  'automation.accounts.noLabel': 'без ярлыка',
  'automation.accounts.actions': 'Действия',
  'automation.accounts.seconds': '{value} с',
  'automation.accounts.ms': '{value} мс',
  'automation.accounts.readiness.ok': 'годится',
  'automation.accounts.readiness.warn': 'с оговорками',
  'automation.accounts.readiness.fail': 'не годится',
  'automation.accounts.readiness.unknown': 'неизвестно',

  // ─── пул ───
  'automation.accounts.pool.search': 'Поиск по пулу',
  'automation.accounts.pool.searchPlaceholder': 'Ярлык, компания, телефон, ИИН',
  'automation.accounts.pool.fitFilter': 'Годится для',
  'automation.accounts.pool.fitAny': 'Годится для: все',
  'automation.accounts.pool.fitFor': 'Годится для: {what}',
  'automation.accounts.pool.col.payments': 'Платежи',
  'automation.accounts.pool.col.inBetween': 'Между счетами',
  'automation.accounts.pool.col.largeSum': 'Крупная сумма',
  'automation.accounts.pool.col.deposit': 'Депозит',
  'automation.accounts.pool.col.salary': 'Зарплата',
  'automation.accounts.pool.col.loan': 'Кредит',
  'automation.accounts.pool.col.cards': 'Карты',
  'automation.accounts.pool.col.ai': 'AI',
  'automation.accounts.pool.checking': 'Проверяем {done} из {total}…',
  'automation.accounts.pool.checkedAll': 'Проверка всех: {when}',
  'automation.accounts.pool.errors': {
    one: '{count} ошибка',
    few: '{count} ошибки',
    many: '{count} ошибок',
    other: '{count} ошибки',
  },
  'automation.accounts.pool.checkAll': 'Проверить все',
  'automation.accounts.pool.checkAllHint':
    'Перепроверяет каждый аккаунт пула на стенде, по одному, в фоне',
  'automation.accounts.pool.checkAllFailed': '«Проверить все» не запустилась',
  'automation.accounts.pool.empty': 'Пул пуст',
  'automation.accounts.pool.emptyHint':
    'Проверьте аккаунт на вкладке «Проверка» или загрузите список кнопкой «Импорт».',
  'automation.accounts.pool.account': 'Аккаунт',
  'automation.accounts.pool.phoneIin': 'Телефон / ИИН',
  'automation.accounts.pool.payer': 'Счёт списания',
  'automation.accounts.pool.payerHint':
    'Основной счёт, а если он не задан — первый открытый тенговый, как в тестах payments',
  'automation.accounts.pool.checked': 'Проверен',
  'automation.accounts.pool.payerMain': 'основной',
  'automation.accounts.pool.noMain': 'основного нет',
  'automation.accounts.pool.noData': 'нет данных',
  'automation.accounts.pool.errorAt': 'ошибка {when}',
  'automation.accounts.pool.noMatch': 'Под фильтр ничего не подошло.',
  'automation.accounts.pool.spent': 'израсходован: {classes}',
  'automation.accounts.pool.spentHint':
    'Выдан одноразовым классам — им больше не предлагается: {list}. «Готовность к тестам» → «Вернуть в оборот».',
  'automation.accounts.pool.repairing': 'чиним: {what}…',
  'automation.accounts.pool.listBroken': 'счета не грузятся — тестам не выдаётся',
  'automation.accounts.pool.listBrokenHint':
    '{reason}. Такой аккаунт не выдаётся тестам: без списка счетов они упадут не по своей причине.',
  'automation.accounts.pool.listBrokenReason': 'список счетов не загружается',

  // ─── импорт ───
  'automation.accounts.import.button': 'Импорт',
  'automation.accounts.import.file': 'Файл CSV',
  'automation.accounts.import.template': 'Шаблон',
  'automation.accounts.import.poolTitle': 'Импорт аккаунтов в пул',
  'automation.accounts.import.poolHint':
    'По строке на аккаунт: телефон; ИИН; БИН; ярлык (БИН — только для ТОО). Подходит CSV из Excel. Аккаунты появятся в пуле сразу, проверка — в фоне, по одному.',
  'automation.accounts.import.poolPlaceholder':
    '+70000000001; 000000000001; ; ИП: карты + депозит\n+70000000002; 000000000002; 000000000003; ТОО: QR',
  'automation.accounts.import.toImport': 'К импорту: {count}',
  'automation.accounts.import.withError': 'с ошибкой: {count}',
  'automation.accounts.import.badLine': 'строка {line}: {reason} — {text}',
  'automation.accounts.import.more': 'и ещё {count}',
  'automation.accounts.import.problem.excelNumber':
    'Excel записал число как 9,00E+11 — сделайте столбцы ИИН и БИН текстовыми',
  'automation.accounts.import.problem.phone': 'телефон',
  'automation.accounts.import.problem.iin': 'ИИН — 12 цифр',
  'automation.accounts.import.problem.bin': 'БИН — 12 цифр',
  'automation.accounts.import.problem.kind': 'вид: IP, TOO, MF или SIMP',
  'automation.accounts.import.problem.companyBin': 'у компании нужен БИН — 12 цифр',
  'automation.accounts.import.addAndCheck': 'Добавить и проверить',
  'automation.accounts.import.add': 'Добавить',
  'automation.accounts.import.failed': 'Импорт не прошёл',
  'automation.accounts.import.poolDone': 'Добавлено {added}, уже были в пуле {updated}',
  'automation.accounts.import.rejected': 'отклонено {count}',
  'automation.accounts.import.checkStarted':
    'Проверка идёт в фоне — прогресс в шапке пула. Кто не войдёт, останется с пометкой «ошибка».',
  'automation.accounts.import.checkWasRunning':
    'Проверка уже шла — новые аккаунты проверьте кнопкой «Проверить все» после неё.',
  'automation.accounts.import.regTitle': 'Импорт в список на регистрацию',
  'automation.accounts.import.regHint':
    'По строке: вид; телефон; ИИН; БИН; ярлык. Вид — IP, TOO, MF или SIMP (прочая ОПФ); БИН — у всех, кроме ИП. Файл даёт make_import.py reg. Те, кто уже в пуле, пропускаются: они зарегистрированы.',
  'automation.accounts.import.regPlaceholder':
    'IP; +70000000001; 000000000001; ; строка 2528\nMF; +70000000002; 000000000002; 000000000003; строка 4410',
  'automation.accounts.import.regDone': 'Добавлено {added}, обновлено {updated}',
  'automation.accounts.import.pooled': 'уже в пуле (зарегистрированы) {count}',
  'automation.accounts.import.templateLine1':
    'Импорт аккаунтов в пул TestRun: по строке на аккаунт. БИН — только для ТОО, у ИП пусто.',
  'automation.accounts.import.templateLine2':
    'Строки с # и заголовок пропускаются. Значения ниже — пример, замените своими.',
  'automation.accounts.import.templateHeader': 'телефон;ИИН;БИН;ярлык',
  'automation.accounts.import.templateLabel1': 'ИП: карты + депозит',
  'automation.accounts.import.templateLabel2': 'ТОО: Freedom QR',
  'automation.accounts.import.templateFile': 'шаблон-импорта-аккаунтов.csv',

  // ─── сторож данных ───
  'automation.accounts.changes.title': 'Изменения в аккаунтах',
  'automation.accounts.changes.history': 'История изменений',
  'automation.accounts.changes.worse': 'из них {count} — тестам стало хуже',
  'automation.accounts.changes.period': 'Период',
  'automation.accounts.changes.days': {
    one: 'Сутки',
    few: '{count} дня',
    many: '{count} дней',
    other: '{count} дня',
  },
  'automation.accounts.changes.none': {
    one: 'За сутки изменений нет.',
    few: 'За {count} дня изменений нет.',
    many: 'За {count} дней изменений нет.',
    other: 'За {count} дня изменений нет.',
  },
  'automation.accounts.changes.watchOn':
    'Сторож проверяет пул каждый день в {at} ({tz}) и только читает данные; последний проход — {last}, следующий — {next}. Ручные проверки тоже попадают сюда.',
  'automation.accounts.changes.watchOff':
    'Ночной проход выключен (WATCH_AT); сюда попадают изменения, найденные ручными проверками.',
  'automation.accounts.changes.never': 'ещё не было',
  'automation.accounts.changes.byWatch': 'ночная проверка',
  'automation.accounts.changes.byUser': 'проверил {user}',
  'automation.accounts.changes.account': 'Аккаунт',
  'automation.accounts.changes.level.bad': 'тестам стало хуже',
  'automation.accounts.changes.level.good': 'снова годится',
  'automation.accounts.changes.level.info': 'к сведению',

  // ─── проверка ───
  'automation.accounts.check.phoneHint': 'С «+» или без, можно через 8 — приведём к +7',
  'automation.accounts.check.binPlaceholder': 'только для ТОО',
  'automation.accounts.check.fresh': 'Войти заново (OTP и биометрия), не брать токен из памяти',
  'automation.accounts.check.submit': 'Проверить',
  'automation.accounts.check.loggingIn': 'Входим в аккаунт…',
  'automation.accounts.check.slowHint':
    'Первый вход занимает до полуминуты: код из SMS берём в Kafdrop, биометрию проходим тестовым режимом.',
  'automation.accounts.check.failed': 'Проверить не удалось',
  'automation.accounts.check.inPool': 'Аккаунт в пуле команды —',
  'automation.accounts.check.openCard': 'открыть карточку, задать ярлык',

  // ─── результат проверки ───
  'automation.accounts.result.noCompany': 'Компания не определена',
  'automation.accounts.result.accounts': 'Счета',
  'automation.accounts.result.cards': 'Карты',
  'automation.accounts.result.cardsOpen': 'Карты · открыто {open}',
  'automation.accounts.result.deposits': 'Депозиты',
  'automation.accounts.result.loans': 'Кредиты',
  'automation.accounts.result.employees': 'Сотрудники',
  'automation.accounts.result.toSign': 'На подпись',
  'automation.accounts.result.fitsFor': 'Годится для',
  'automation.accounts.result.account': 'Счёт',
  'automation.accounts.result.number': 'Номер',
  'automation.accounts.result.balance': 'Остаток',
  'automation.accounts.result.marks': 'Признаки',
  'automation.accounts.result.main': 'основной',
  'automation.accounts.result.special': 'спецсчёт',
  'automation.accounts.result.blocked': 'заблокирован',
  'automation.accounts.result.accountsNotLoaded': 'Счета не загрузились',
  'automation.accounts.result.accountsNone': 'Счетов нет',
  'automation.accounts.result.loansNotLoaded': 'Кредиты не загрузились',
  'automation.accounts.result.loansNone': 'Кредитов нет',
  'automation.accounts.result.cardsNotLoaded': 'Карты не загрузились',
  'automation.accounts.result.cardsNone': 'Карт нет',
  'automation.accounts.result.depositsNotLoaded': 'Депозиты не загрузились',
  'automation.accounts.result.depositsNone': 'Депозитов нет',
  'automation.accounts.result.loanClosed': 'закрыт',
  'automation.accounts.result.loanActive': 'действует',
  'automation.accounts.result.overdue': 'просрочка',
  'automation.accounts.result.debt': 'долг {amount}',
  'automation.accounts.result.nextPayment': 'платёж {amount}',
  'automation.accounts.result.onDay': 'на {day}',
  'automation.accounts.result.paid': 'оплачено {paid} из {total}',
  'automation.accounts.result.forAutotests': 'Для автотестов',
  'automation.accounts.result.counterparties': 'Контрагенты',
  'automation.accounts.result.aiOnboarding': 'AI-онбординг',
  'automation.accounts.result.done': 'пройден',
  'automation.accounts.result.notDone': 'не пройден',
  'automation.accounts.result.aiLimits': 'AI-лимиты',
  'automation.accounts.result.notSet': 'не заданы',
  'automation.accounts.result.endpoints': 'Ручки стенда',
  'automation.accounts.result.requests': {
    one: '{count} запрос',
    few: '{count} запроса',
    many: '{count} запросов',
    other: '{count} запроса',
  },
  'automation.accounts.result.not200': 'не 200 — {count}',
  'automation.accounts.result.all200': 'все 200',
  'automation.accounts.result.error': 'ошибка',
  'automation.accounts.result.cannotShow': 'Не удалось показать результат',
  'automation.accounts.result.cannotShowHint':
    '{message}. Пришлите текст ошибки — значит, стенд отдал поле в неожиданном виде.',

  // ─── действия на стенде ───
  'automation.accounts.act.title': 'Действия',
  'automation.accounts.act.subtitle':
    'Настоящие операции на стенде с подписью OTP; деньги остаются в компании. Балансы — на момент проверки.',
  'automation.accounts.act.failed': 'Не получилось',
  'automation.accounts.act.waitingOtp': 'Ждём OTP…',
  'automation.accounts.act.realPaymentOtp':
    'Настоящий платёж на стенде: придёт OTP на номер аккаунта.',
  'automation.accounts.act.realTransferOtp': 'Настоящий перевод на стенде, подпись OTP.',
  'automation.accounts.act.refreshBalances': 'Обновить балансы',
  'automation.accounts.act.refreshCheck': 'Обновить проверку',
  'automation.accounts.act.paymentDone': '{amount}: «{from}» → «{to}» — {status}',
  'automation.accounts.act.total': 'всего {time}',
  'automation.accounts.act.transfer.title': 'Перевод между своими счетами',
  'automation.accounts.act.transfer.hint':
    'IN_BETWEEN, КНП 342, подпись OTP — как регресс payments.',
  'automation.accounts.act.transfer.from': 'Откуда',
  'automation.accounts.act.transfer.to': 'Куда',
  'automation.accounts.act.transfer.amount': 'Сумма',
  'automation.accounts.act.transfer.go': 'Перевести',
  'automation.accounts.act.transfer.needTwo': 'Нужно два открытых счёта в одной валюте',
  'automation.accounts.act.transfer.positive': 'Сумма — положительное число',
  'automation.accounts.act.transfer.notEnough': 'На «{account}» меньше {amount}',
  'automation.accounts.act.transfer.confirm': 'Перевести {amount} с «{from}» на «{to}»?',
  'automation.accounts.act.transfer.failed': 'Перевод не прошёл',
  'automation.accounts.act.transfer.noAccounts': 'нет открытых счетов',
  'automation.accounts.act.transfer.noSameCurrency': 'нет счёта в той же валюте',
  'automation.accounts.act.topUp.title': 'Долить счёт списания',
  'automation.accounts.act.topUp.hint':
    'Для крупной суммы. «{account}»{main}: {balance}; донор — самый богатый свой счёт, на нём остаётся 1 ₸.',
  'automation.accounts.act.topUp.isMain': ' (основной)',
  'automation.accounts.act.topUp.target': 'До суммы',
  'automation.accounts.act.topUp.presetIp': '6 млн',
  'automation.accounts.act.topUp.presetIpHint': 'крупная сумма ИП: порог 5 000 001 ₸',
  'automation.accounts.act.topUp.presetToo': '55 млн',
  'automation.accounts.act.topUp.presetTooHint': 'крупная сумма ТОО: порог 50 000 000 ₸',
  'automation.accounts.act.topUp.go': 'Долить',
  'automation.accounts.act.topUp.running': 'Переводим…',
  'automation.accounts.act.topUp.noPayer': 'Нет открытого тенгового счёта',
  'automation.accounts.act.topUp.positive': 'Цель — положительное число',
  'automation.accounts.act.topUp.enough': 'На счёте списания уже {amount} — не меньше цели',
  'automation.accounts.act.topUp.noDonor':
    'Не с чего доливать: других открытых счетов с деньгами в той же валюте нет',
  'automation.accounts.act.topUp.plan': 'Переведём {amount} с «{donor}»',
  'automation.accounts.act.topUp.planShort':
    '; до цели не хватит ещё {amount} — следующее нажатие возьмёт следующий счёт',
  'automation.accounts.act.topUp.confirm': 'Долить счёт списания «{account}» до {amount}?',
  'automation.accounts.act.topUp.failed': 'Долить не удалось',
  'automation.accounts.act.topUp.result':
    'Счёт списания: было {before}, стало ≈ {after}, цель {target}.',
  'automation.accounts.act.topUp.shortNext':
    'Не хватает {amount} — нажмите ещё раз, следующий донор «{donor}».',
  'automation.accounts.act.topUp.shortNone': 'Не хватает {amount} — других счетов с деньгами нет.',
  'automation.accounts.act.main.title': 'Основной счёт',
  'automation.accounts.act.main.hint':
    'Сейчас: {current}. Нужен для AI — ассистент списывает с основного; платежам и остальным тестам не нужен.',
  'automation.accounts.act.main.unset': 'не задан',
  'automation.accounts.act.main.choose': 'Сделать основным',
  'automation.accounts.act.main.noOther': 'нет другого открытого тенгового счёта',
  'automation.accounts.act.main.set': 'Сделать основным',
  'automation.accounts.act.main.remove': 'Убрать',
  'automation.accounts.act.main.removeConfirm': 'Снять признак «основной» со счёта «{account}»?',
  'automation.accounts.act.main.removeHint':
    'Тесты AI списывают с основного счёта и без него падают. Без OTP.',
  'automation.accounts.act.main.removed': 'Признак снят с «{account}»',
  'automation.accounts.act.main.isNow': 'Основной — «{account}»',
  'automation.accounts.act.set.title': 'Набор пробных платежей',
  'automation.accounts.act.set.hint':
    'Свои счета, карта SuperApp, депозит туда и обратно, налог по КБК, зарплата. Тип без данных пропускается.',
  'automation.accounts.act.set.start': 'Запустить',
  'automation.accounts.act.set.again': 'Ещё раз',
  'automation.accounts.act.set.running': 'Идёт…',
  'automation.accounts.act.set.confirm':
    'Провести набор пробных платежей: свои счета, своя карта SuperApp, депозит туда и обратно, налог по КБК, зарплата одному сотруднику?',
  'automation.accounts.act.set.confirmHint':
    'Шесть настоящих платежей на стенде по 10–100 ₸, на каждый — OTP. Около минуты.',
  'automation.accounts.act.set.notStarted': 'Набор не запустился',
  'automation.accounts.act.set.lost': 'Потеряли прогон',
  'automation.accounts.act.set.noPayments': 'Не дошли до платежей',
  'automation.accounts.act.set.itemRunning': 'создаём, ждём OTP, подписываем…',
  'automation.accounts.act.set.itemPending': 'ждёт',
  'automation.accounts.act.set.skipped': 'пропущен: {reason}',
  'automation.accounts.act.set.summary': 'Прошло {ok} из {total}',
  'automation.accounts.act.set.failedCount': 'упало {count}',
  'automation.accounts.act.set.skippedCount': 'пропущено {count}',
  'automation.accounts.act.set.status.pending': 'ждёт',
  'automation.accounts.act.set.status.running': 'идёт',
  'automation.accounts.act.set.status.ok': 'прошёл',
  'automation.accounts.act.set.status.fail': 'упал',
  'automation.accounts.act.set.status.skip': 'пропущен',
  'automation.accounts.act.cleanup.title': 'Уборка',
  'automation.accounts.act.cleanup.hint':
    'Неподписанные платежи (NEW) от упавших тестов и черновики. Сначала покажем, что нашли.',
  'automation.accounts.act.cleanup.find': 'Найти',
  'automation.accounts.act.cleanup.refresh': 'Обновить',
  'automation.accounts.act.cleanup.listFailed': 'Список не загрузился',
  'automation.accounts.act.cleanup.clean': 'Чисто: неподписанных платежей и черновиков нет.',
  'automation.accounts.act.cleanup.unsigned': 'Неподписанные · {count}',
  'automation.accounts.act.cleanup.drafts': 'Черновики · {count}',
  'automation.accounts.act.cleanup.payment': 'платёж',
  'automation.accounts.act.cleanup.draft': 'черновик',
  'automation.accounts.act.cleanup.fresh': 'моложе 2 минут — не тронем',
  'automation.accounts.act.cleanup.cancelN': {
    one: 'отменить {count} неподписанный',
    few: 'отменить {count} неподписанных',
    many: 'отменить {count} неподписанных',
    other: 'отменить {count} неподписанных',
  },
  'automation.accounts.act.cleanup.deleteN': {
    one: 'удалить {count} черновик',
    few: 'удалить {count} черновика',
    many: 'удалить {count} черновиков',
    other: 'удалить {count} черновика',
  },
  'automation.accounts.act.cleanup.confirm': 'Точно {what}?',
  'automation.accounts.act.cleanup.confirmHint':
    'Это не откатить. Если по аккаунту сейчас идёт автотест — подождите, пока он закончится.',
  'automation.accounts.act.cleanup.go': 'Убрать',
  'automation.accounts.act.cleanup.failed': 'Уборка не прошла',
  'automation.accounts.act.cleanup.result': 'Отменено {cancelled}, удалено черновиков {deleted}',
  'automation.accounts.act.cleanup.resultSkipped': 'пропущено {count}',
  'automation.accounts.act.cleanup.resultFailed': {
    one: '{count} ошибка',
    few: '{count} ошибки',
    many: '{count} ошибок',
    other: '{count} ошибки',
  },
  'automation.accounts.act.otp.title': 'Код OTP для ручного входа',
  'automation.accounts.act.otp.hint':
    'Запросите код в приложении на {phone} — покажем последний из Kafdrop.',
  'automation.accounts.act.otp.show': 'Показать код',
  'automation.accounts.act.otp.refresh': 'Обновить',
  'automation.accounts.payStatus.IN_PROGRESS_AT_BANK': 'подписан, в обработке банком',
  'automation.accounts.payStatus.SIGNED': 'подписан',
  'automation.accounts.payStatus.SUCCEED': 'проведён',
  'automation.accounts.payStatus.NEW': 'создан, не подписан',
  'automation.accounts.payStatus.unknown': 'статус неизвестен',

  // ─── вкладка OTP ───
  'automation.accounts.otp.submit': 'Показать последний код',
  'automation.accounts.otp.hint':
    'Для ручного теста: запросите код в приложении — он появится здесь (берём из Kafdrop, за последние 10 минут). Три минуты страница обновляется сама.',
  'automation.accounts.otp.none': 'За 10 минут кода на этот номер не было',
  'automation.accounts.otp.code': 'Код OTP',
  'automation.accounts.otp.failed': 'Код не получили',
  'automation.accounts.otp.watching': 'обновляется',
  'automation.accounts.otp.asking': 'запрашиваем…',

  // ─── карточка аккаунта ───
  'automation.accounts.detail.back': 'Пул аккаунтов',
  'automation.accounts.detail.noLabel': 'Без ярлыка',
  'automation.accounts.detail.label': 'Ярлык',
  'automation.accounts.detail.labelPlaceholder':
    'например, «основной ИП payments», «AI», «ТОО 50 млн»',
  'automation.accounts.detail.editLabel': 'Изменить',
  'automation.accounts.detail.setLabel': 'Задать ярлык',
  'automation.accounts.detail.inPoolSince': 'В пуле: добавлен {when} ({by})',
  'automation.accounts.detail.checked': 'проверен {when}',
  'automation.accounts.detail.recheck': 'Перепроверить',
  'automation.accounts.detail.rechecking': 'Проверяем…',
  'automation.accounts.detail.recheckHint':
    'Снова входит в аккаунт на стенде; проверка договора ТСП создаёт и отменяет QR на 1 ₸',
  'automation.accounts.detail.remove': 'Из пула',
  'automation.accounts.detail.removeConfirm': 'Убрать аккаунт из пула?',
  'automation.accounts.detail.removeHint': 'На стенде с ним ничего не случится.',
  'automation.accounts.detail.removed': 'Аккаунт убран из пула',
  'automation.accounts.detail.lastFailed': 'Последняя проверка не прошла',
  'automation.accounts.detail.noCheck': 'Успешной проверки ещё не было.',

  // ─── регистрация ───
  'automation.accounts.reg.kind.IP': 'ИП',
  'automation.accounts.reg.kind.TOO': 'ТОО',
  'automation.accounts.reg.kind.MF': 'ТОО, неск. учредителей',
  'automation.accounts.reg.kind.SIMP': 'прочая ОПФ',
  'automation.accounts.reg.free': 'свободно {count}',
  'automation.accounts.reg.nextFree': 'Следующий свободный',
  'automation.accounts.reg.nextFreeHint':
    'Заполнить форму следующим свободным аккаунтом из списка «На регистрацию»',
  'automation.accounts.reg.phoneHint': 'С «+» или без, можно через 8',
  'automation.accounts.reg.needTitle': 'Что нужно',
  'automation.accounts.reg.need': 'ИИН и телефон, ещё не зарегистрированные в Freedom Business.',
  'automation.accounts.reg.need.IP':
    'ИИН — действующего ИП, иначе заявку отклонит проверка статуса в КГД.',
  'automation.accounts.reg.need.TOO':
    'Для ТОО ещё БИН; ИИН, только что зарегистрированный как ИП, не подойдёт. Шаг eGov ждёт код с 1414 на настоящий телефон директора.',
  'automation.accounts.reg.need.MF':
    'Директор ТОО с несколькими учредителями: проверка без OTP отвечает LLP_004_GBDUL. У ТОО — открытый счёт.',
  'automation.accounts.reg.need.SIMP':
    'Руководитель компании прочей ОПФ (АО, кооператив, учреждение — не ИП и не ТОО) и её БИН, у компании открытый счёт. Обычному ТОО сервис ответит LLP_006_GBDUL.',
  'automation.accounts.reg.needTail':
    'Заявка, ушедшая на ручную проверку AML, автотестом не закрывается. Прогон идёт пайплайном authenticate_api_testing ({className}), 2–4 минуты.',
  'automation.accounts.reg.project': 'Проект: {name}',
  'automation.accounts.reg.submit': 'Зарегистрировать',
  'automation.accounts.reg.starting': 'Запускаем…',
  'automation.accounts.reg.confirm': 'Зарегистрировать {kind} на preprod: {who}?',
  'automation.accounts.reg.confirmHint':
    'Будет создан настоящий клиент. После успеха ИИН занят: повторная регистрация с ним упадёт на первом шаге.',
  'automation.accounts.reg.marksError': 'Прогон запущен, но TestRun не узнал об аккаунте: {error}',
  'automation.accounts.reg.noProject': 'Проект регистрации не подключён',
  'automation.accounts.reg.noProjectHint':
    'Добавьте authenticate_api_testing в одну из команд: регистрация запускает его тесты RegistrationIe, RegistrationLlp, RegistrationLlpMultiFounder и RegistrationSimplifiedAccess.',
  'automation.accounts.reg.projectsFailed': 'Проекты не загрузились',
  'automation.accounts.reg.listTitle': 'На регистрацию',
  'automation.accounts.reg.listHint':
    'Незарегистрированные клиенты для классов регистрации. Окно запуска выдаёт их само (testdata registration), после прогона TestRun проверяет без OTP, создан ли клиент: да — аккаунт уходит в пул, нет — снова свободен.',
  'automation.accounts.reg.settle': 'Проверить выданные',
  'automation.accounts.reg.settleCount': 'Проверить выданные ({count})',
  'automation.accounts.reg.settleHint': 'Проверить выданные, чей прогон закончился',
  'automation.accounts.reg.counts': '{kind}: свободно {free}',
  'automation.accounts.reg.countIssued': 'выдано {count}',
  'automation.accounts.reg.countRegistered': 'зарег. {count}',
  'automation.accounts.reg.statusFilter': 'Статус',
  'automation.accounts.reg.allStatuses': 'Все статусы',
  'automation.accounts.reg.status.free': 'свободен',
  'automation.accounts.reg.status.issued': 'выдан',
  'automation.accounts.reg.status.registered': 'зарегистрирован',
  'automation.accounts.reg.status.used': 'израсходован',
  'automation.accounts.reg.status.unsuitable': 'не подходит',
  'automation.accounts.reg.statusHint.free': 'не зарегистрирован — можно выдать',
  'automation.accounts.reg.statusHint.issued': 'выдан прогону; после него TestRun проверит итог',
  'automation.accounts.reg.statusHint.registered': 'клиент создан — аккаунт уходит в пул',
  'automation.accounts.reg.statusHint.used': 'итог прогона не проверить без OTP — решите вручную',
  'automation.accounts.reg.statusHint.unsuitable': 'регистрация этим аккаунтом невозможна',
  'automation.accounts.reg.empty':
    'Список пуст. Кандидатов отбирает bot/var/clients: pick.py → probe.mjs → make_import.py reg; получившийся CSV загрузите кнопкой «Импорт».',
  'automation.accounts.reg.kindColumn': 'Вид',
  'automation.accounts.reg.who': 'Телефон / ИИН / БИН',
  'automation.accounts.reg.lastCheck': 'Последняя проверка',
  'automation.accounts.reg.inPool': 'в пуле',
  'automation.accounts.reg.poolError': 'в пул не добавлен: {error}',
  'automation.accounts.reg.verifiedYes': 'зарегистрирован',
  'automation.accounts.reg.verifiedNo': 'не зарегистрирован',
  'automation.accounts.reg.check': 'Проверить',
  'automation.accounts.reg.checkHint': 'authenticate без OTP: SMS не уходит',
  'automation.accounts.reg.release': 'Вернуть',
  'automation.accounts.reg.releaseHint': 'Снова выдавать: регистрация этим аккаунтом не прошла',
  'automation.accounts.reg.remove': 'Убрать',
  'automation.accounts.reg.removeLabel': 'Убрать {phone} из списка',
  'automation.accounts.reg.removeConfirm': 'Убрать {phone} из списка на регистрацию?',
  'automation.accounts.reg.removeHint': 'На стенде с ним ничего не случится.',
  'automation.accounts.reg.noneWithStatus': 'С таким статусом аккаунтов нет.',

  // ─── готовность к тестам ───
  'automation.accounts.readinessTab.project': 'Проект',
  'automation.accounts.readinessTab.noProjects': 'Проектов нет',
  'automation.accounts.readinessTab.noProjectsHint':
    'Готовность считается по классам проекта: сначала добавьте проект в команду.',
};
